import React from "react"

import { faFlag } from "@fortawesome/free-solid-svg-icons"

import { PayerStatus } from "@common-shared/config/oneVerify/oneVerifyPayerStatus.config"
import type { IOneVerifyPayerStatusPayer } from "@common-shared/types/responseData/oneVerifyResponseData.types"

import { NIRVANA_COLORS } from "@front-shared/config/theme.config"
import { PrimaryText } from "@front-shared/elements/primaryText.element"

import { GENERAL_CONFIG } from "../../config/general.config"
import { PAYER_STATUS_CONFIG } from "../../config/payerStatus.config"
import { PayerStatusSummaryCardComponent } from "./payerStatusSummaryCard.component"
import { PayerStatusUpdatedComponent } from "./payerStatusUpdated.component"

export const PayerStatusSummaryComponent: React.FunctionComponent<
    IPayerStatusSummaryComponentProps
> = props => {
    const operationalConfig =
        PAYER_STATUS_CONFIG.statuses[PayerStatus.OPERATIONAL]

    const issueConfig = PAYER_STATUS_CONFIG.statuses[PayerStatus.ISSUE_DETECTED]

    const flaggedCount = props.payers.filter(
        row => row.status === PayerStatus.ISSUE_DETECTED,
    ).length

    // Card click toggles its status filter
    const onCardClick = (status: PayerStatus) => {
        props.onStatusFilterChange(
            props.statusFilter === status ? null : status,
        )
    }

    return (
        <section
            aria-label="Current status"
            className={`flex flex-col gap-[12px] ${props.className || ""}`}
        >
            <div className="flex flex-wrap items-baseline justify-between gap-x-[12px] gap-y-[4px]">
                <PrimaryText typography="h5">Current status</PrimaryText>

                <PayerStatusUpdatedComponent
                    fetchedAt={props.fetchedAt}
                    isLoading={props.isLoading}
                    isStale={props.isStale}
                />
            </div>

            <div className="grid grid-cols-[repeat(auto-fit,minmax(260px,1fr))] gap-[16px]">
                <PayerStatusSummaryCardComponent
                    label={operationalConfig.label}
                    icon={operationalConfig.icon}
                    color={operationalConfig.color}
                    count={props.payers.length - flaggedCount}
                    filterLabel={operationalConfig.label}
                    isSelected={props.statusFilter === PayerStatus.OPERATIONAL}
                    onClick={() => {
                        onCardClick(PayerStatus.OPERATIONAL)
                    }}
                >
                    No issues detected. If you’re still seeing problems,{" "}
                    <a
                        className="text-semibold whitespace-nowrap !text-brand-vibrantPurple !underline focus-visible:!outline-2 focus-visible:!outline-offset-2 focus-visible:!outline-brand-vibrantPurple"
                        href={`mailto:${GENERAL_CONFIG.supportEmail}`}
                    >
                        contact support
                    </a>
                    .
                </PayerStatusSummaryCardComponent>

                <PayerStatusSummaryCardComponent
                    label="Flagged"
                    icon={faFlag}
                    // Calm when nothing is flagged
                    color={
                        flaggedCount
                            ? issueConfig.color
                            : NIRVANA_COLORS.brand.grey7
                    }
                    count={flaggedCount}
                    link={
                        flaggedCount
                            ? {
                                  label: `See affected ${flaggedCount === 1 ? "payer" : "payers"}`,
                                  onClick: props.onAffectedClick,
                              }
                            : undefined
                    }
                    filterLabel={issueConfig.label}
                    isSelected={
                        props.statusFilter === PayerStatus.ISSUE_DETECTED
                    }
                    onClick={() => {
                        onCardClick(PayerStatus.ISSUE_DETECTED)
                    }}
                >
                    A payer is flagged when more than half of eligibility
                    requests fail, with at least 200 requests in a 2-hour
                    window.
                    {flaggedCount
                        ? " Checks for flagged payers may fail until the issue is resolved. Check this page for real-time updates."
                        : ""}
                </PayerStatusSummaryCardComponent>
            </div>
        </section>
    )
}

interface IPayerStatusSummaryComponentProps {
    className?: string
    payers: IOneVerifyPayerStatusPayer[]
    fetchedAt: number
    isLoading: boolean // refresh the customer asked for
    isStale: boolean
    statusFilter: PayerStatus | null
    onStatusFilterChange: (status: PayerStatus | null) => void
    onAffectedClick: () => void
}
